import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const DriverTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [drivers, setDrivers] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.drivers || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchDrivers = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/drivers');
            setDrivers(data.drivers);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load drivers", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/driver/${id}`);
            enqueueSnackbar("Driver removed", { variant: "success" });
            setDrivers((prev) => prev.filter((d) => d._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to remove driver", { variant: "error" });
        }
    };

    const [sendingEmailId, setSendingEmailId] = useState(null);

    const sendAppEmailHandler = async (id) => {
        setSendingEmailId(id);
        try {
            await axios.put(`/api/v1/admin/driver/${id}/send-app-email`);
            enqueueSnackbar("App download email sent", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to send email", { variant: "error" });
        } finally {
            setSendingEmailId(null);
        }
    };

    const columns = [
        {
            field: "name",
            headerName: "Name",
            minWidth: 160,
            flex: 0.5,
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 200,
            flex: 0.6,
        },
        {
            field: "phoneNo",
            headerName: "Phone",
            minWidth: 130,
            flex: 0.4,
        },
        {
            field: "isAvailable",
            headerName: "Availability",
            minWidth: 120,
            flex: 0.4,
            renderCell: (params) => (
                params.row.isAvailable
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Available</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Unavailable</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 150,
            flex: 0.4,
            sortable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <Actions editRoute={"driver"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.name} canEdit={canEdit} canDelete={canDelete} />
                    {canEdit && (
                        <Tooltip title="Email app download link to driver">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => sendAppEmailHandler(params.row.id)}
                                    disabled={sendingEmailId === params.row.id}
                                >
                                    {sendingEmailId === params.row.id ? <CircularProgress size={18} /> : <MailOutlineIcon sx={{ fontSize: "20px" }} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    const rows = drivers.map((d) => ({
        id: d._id,
        name: d.name,
        email: d.email,
        phoneNo: d.phoneNo || "—",
        isAvailable: d.isAvailable,
    }));

    return (
        <>
            <MetaData title="Admin: Drivers | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Drivers</h1>
                {canCreate && (
                    <Link to="/admin/driver/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Driver</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 500 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default DriverTable;